/**
 * Pure helpers for MapLibre transit stop sources, layers, and GeoJSON mapping.
 */

import type { Feature, FeatureCollection } from "geojson";
import type maplibregl from "maplibre-gl";

import { MAP_STOPS_LAYER_ZOOM } from "../../../config/global.configuration";
import stopChildIcon from "../../../image/icons/map/stopPlatform/stopChildIcon.svg";
import stopParentIcon from "../../../image/icons/map/stopPlatform/stopParentIcon.svg";
import { colorPalette } from "../../../theme/ColorPalette";
import { formatStopName, formatStopNameShort } from "../../../tools/stopNameFormatter";
import type { StopDto } from "../../../types/dtos/api/stop.dto";
import {
    STOP_CHILD_ICON_IMAGE_ID,
    STOP_LABEL_TEXT_LAYER_IDS,
    STOP_PARENT_ICON_IMAGE_ID,
    STOPS_CHILDREN_SOURCE_ID,
    STOPS_LAYER_CHILDREN_ID,
    STOPS_LAYER_CHILDREN_NAME_ID,
    STOPS_LAYER_CHILDREN_PLATFORM_ID,
    STOPS_LAYER_PARENTS_ID,
    STOPS_PARENTS_SOURCE_ID,
} from "../mapLayerIds";
import type { StopFeatureVariant } from "./stopMapLayers.types";
import type { PreloadedStopGroups } from "./stopPreload.types";

/** Empty GeoJSON used when stops cannot be loaded (e.g. spatial index not ready). */
export const EMPTY_STOPS_FEATURE_COLLECTION: FeatureCollection = {
    "type":     "FeatureCollection",
    "features": [],
};

/** Text color for stop label layers in light UI mode. */
const STOP_LABEL_TEXT_COLOR_LIGHT = "#0a8f6a";

/** Text halo color for stop label layers in light UI mode. */
const STOP_LABEL_TEXT_HALO_COLOR_LIGHT = "#ffffff";

/** Text halo color for stop label layers in dark UI mode (rgb(46, 61, 86)). */
export const STOP_LABEL_TEXT_HALO_COLOR_DARK = "#2e3d56";

/** Light-background fills baked into stop pole SVGs (replaced in dark UI). */
const STOP_ICON_LIGHT_BACKGROUND_COLORS = [
    "#ffffff",
    "#f8fefd",
] as const;

/**
 * Legacy pole icon vertical offset (ems) for platform stop icons.
 * Kept as-is after visual validation — restore `[0, 100]` if pole anchor looks wrong after refactors.
 * Related: icon-anchor `"bottom"`, separate name/platform text layers above/below.
 */
export const STOP_CHILD_ICON_OFFSET: [number, number] = [ 0, 100 ];

/** Bundled SVG asset URLs for stop pole icons. */
export const STOP_ICON_ASSETS = {
    "parent": stopParentIcon,
    "child":  stopChildIcon,
} as const;

const { parentStationsMinZoom, stopPlatformsMinZoom } = MAP_STOPS_LAYER_ZOOM;

/** Max zoom for parent layers (exclusive upper bound for platform mode). */
const PARENT_STOPS_MAX_ZOOM = stopPlatformsMinZoom - 0.001;

/** Fixed label size for parent station names (not zoom-interpolated). */
const STOP_PARENT_LABEL_TEXT_SIZE = 11;

/** Fixed label size for platform stop name and quai labels (not zoom-interpolated). */
const STOP_CHILD_LABEL_TEXT_SIZE = 10;

/** Shared symbol text font stack for stop labels. */
const STOP_LABEL_TEXT_FONT: [string, ...string[]] = [
    "Open Sans Semibold",
    "Arial Unicode MS Bold",
];

/**
 * Resolves the text color for stop label MapLibre layers.
 *
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Hex color for `text-color`.
 */
export const getStopLabelTextColor = ( isDarkUi: boolean ): string => (
    isDarkUi ? colorPalette.dark.statusOnTimeText : STOP_LABEL_TEXT_COLOR_LIGHT
);

/**
 * Resolves the text halo color for stop label MapLibre layers.
 *
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Hex color for `text-halo-color`.
 */
export const getStopLabelTextHaloColor = ( isDarkUi: boolean ): string => (
    isDarkUi ? STOP_LABEL_TEXT_HALO_COLOR_DARK : STOP_LABEL_TEXT_HALO_COLOR_LIGHT
);

/**
 * Replaces light pole background colors in stop icon SVG markup.
 *
 * @param svgMarkup Raw SVG source loaded from the bundled asset URL.
 * @param backgroundColor Dark UI replacement color for pole backgrounds and halos.
 * @returns SVG markup with light backgrounds swapped to {@link backgroundColor}.
 */
const tintStopIconSvgForDarkUi = ( svgMarkup: string, backgroundColor: string ): string => {
    let tintedSvg = svgMarkup;

    for ( const lightBackgroundColor of STOP_ICON_LIGHT_BACKGROUND_COLORS ) {
        tintedSvg = tintedSvg.replaceAll( lightBackgroundColor, backgroundColor );
        tintedSvg = tintedSvg.replaceAll( lightBackgroundColor.toUpperCase(), backgroundColor );
    }

    return tintedSvg;
};

/**
 * Registers a decoded stop pole bitmap on the MapLibre style.
 *
 * @param map Target MapLibre map instance.
 * @param imageId MapLibre sprite id to register.
 * @param img Decoded image element.
 */
const registerStopIconOnMap = (
    map: maplibregl.Map,
    imageId: string,
    img: HTMLImageElement,
): void => {
    if ( map.hasImage( imageId ) ) {
        map.removeImage( imageId );
    }

    map.addImage( imageId, img );
};

/**
 * Registers {@link imageId} when the style accepts custom images (retries on `idle` / `style.load`).
 *
 * @param map Target MapLibre map instance.
 * @param imageId MapLibre sprite id to register.
 * @param img Decoded image element.
 */
const registerStopIconWhenMapReady = async(
    map: maplibregl.Map,
    imageId: string,
    img: HTMLImageElement,
): Promise<void> => {
    const tryRegister = (): boolean => {
        try {
            registerStopIconOnMap( map, imageId, img );

            return true;
        } catch {
            return false;
        }
    };

    if ( tryRegister() ) {
        return;
    }

    await new Promise<void>( ( resolve, reject ) => {
        const onStyleReady = (): void => {
            if ( !tryRegister() ) {
                return;
            }

            map.off( "idle", onStyleReady );
            map.off( "style.load", onStyleReady );
            resolve();
        };

        map.on( "idle", onStyleReady );
        map.on( "style.load", onStyleReady );

        const onMapError = ( event: maplibregl.ErrorEvent ): void => {
            map.off( "idle", onStyleReady );
            map.off( "style.load", onStyleReady );
            map.off( "error", onMapError );

            const mapError = event.error;
            const mapErrorMessage = mapError instanceof Error ? mapError.message : "unknown";

            reject( new Error( `Map error while registering stop icon ${ imageId }: ${ mapErrorMessage }` ) );
        };

        map.on( "error", onMapError );
    } );
};

/**
 * Loads a stop pole SVG into MapLibre, tinting light backgrounds in dark UI.
 *
 * @param map Target MapLibre map instance.
 * @param svgUrl Bundled SVG asset URL.
 * @param imageId MapLibre sprite id to register.
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Promise resolved once the image is registered on the map.
 */
export const loadStopIconSvgAsMapImage = async(
    map: maplibregl.Map,
    svgUrl: string,
    imageId: string,
    isDarkUi: boolean,
): Promise<void> => {
    let imageSrc = svgUrl;
    let objectUrl: string | null = null;

    if ( isDarkUi ) {
        const response = await fetch( svgUrl );
        if ( !response.ok ) {
            throw new Error( `Failed to fetch stop icon SVG: ${ svgUrl }` );
        }

        const svgMarkup = await response.text();
        const tintedSvg = tintStopIconSvgForDarkUi( svgMarkup, STOP_LABEL_TEXT_HALO_COLOR_DARK );
        objectUrl       = URL.createObjectURL( new Blob( [ tintedSvg ], { "type": "image/svg+xml" } ) );
        imageSrc        = objectUrl;
    }

    try {
        const img = await new Promise<HTMLImageElement>( ( resolve, reject ) => {
            const imageElement = new Image();
            imageElement.onload = () => {
                resolve( imageElement );
            };
            imageElement.onerror = () => {
                reject( new Error( `Failed to load stop icon image: ${ imageId }` ) );
            };
            imageElement.src = imageSrc;
        } );

        await registerStopIconWhenMapReady( map, imageId, img );
    } finally {
        if ( objectUrl !== null ) {
            URL.revokeObjectURL( objectUrl );
        }
    }
};

/**
 * Maps a {@link StopDto} to a GeoJSON point feature for parent or platform layers.
 *
 * @param stop Stop from the spatial index.
 * @param variant `"parent"` for station overview, `"child"` for platform stops.
 * @returns Point feature for the corresponding GeoJSON source.
 */
export const stopDtoToFeature = ( stop: StopDto, variant: StopFeatureVariant ): Feature => {
    const stopName = stop.stopName ?? "";

    const properties: Record<string, string | boolean> = {
        "id":         stop.stopId,
        "name":       stopName,
        "label":      formatStopName( stopName ),
        "labelShort": formatStopNameShort( stopName ),
        "isParent":   stop.isParent,
    };

    if ( variant === "child" ) {
        properties.platform = stop.platformCode ?? "";

        // Passed through GeoJSON for popup click payload only (not rendered on map labels).
        if ( stop.wheelchairBoarding != null ) {
            properties.wheelchairBoarding = String( stop.wheelchairBoarding );
        }
    }

    return {
        "type":     "Feature",
        "geometry": {
            "type":        "Point",
            "coordinates": [
                stop.stopLon + ( stop.displayLonDelta ?? 0 ),
                stop.stopLat + ( stop.displayLatDelta ?? 0 ),
            ],
        },
        "properties": properties,
    };
};

/**
 * Converts preloaded stop DTO groups to GeoJSON feature collections for MapLibre sources.
 *
 * @param groups Parent and child stop arrays from the preload store.
 * @returns Feature collections for parents and children sources.
 */
export const stopsDtosToFeatureCollections = ( groups: PreloadedStopGroups ): {
    "parents":  FeatureCollection;
    "children": FeatureCollection;
} => ( {
    "parents": {
        "type":     "FeatureCollection",
        "features": groups.parents.map( ( stop ) => stopDtoToFeature( stop, "parent" ) ),
    },
    "children": {
        "type":     "FeatureCollection",
        "features": groups.children.map( ( stop ) => stopDtoToFeature( stop, "child" ) ),
    },
} );

/**
 * Reads `isParent` from a MapLibre feature property (boolean or serialized string).
 *
 * @param value Raw `properties.isParent` from a clicked feature.
 * @returns Whether the stop is a parent station.
 */
export const parseGeoJsonIsParentProperty = ( value: unknown ): boolean => value === true || value === "true";

/** Paint properties shared by stop text label layers. */
const buildStopLabelPaint = ( isDarkUi: boolean ): maplibregl.SymbolLayerSpecification["paint"] => ( {
    "text-color":      getStopLabelTextColor( isDarkUi ),
    "text-halo-color": getStopLabelTextHaloColor( isDarkUi ),
    "text-halo-width": 2,
} );

/**
 * Builds MapLibre layer specifications for all stop symbol layers.
 * Visibility by zoom is handled declaratively via `minzoom` / `maxzoom`.
 *
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Ordered layer specs to add to the map.
 */
export const buildStopLayerSpecs = ( isDarkUi: boolean ): maplibregl.SymbolLayerSpecification[] => {
    const labelPaint = buildStopLabelPaint( isDarkUi );

    return [
        {
            "id":      STOPS_LAYER_PARENTS_ID,
            "type":    "symbol",
            "source":  STOPS_PARENTS_SOURCE_ID,
            "minzoom": parentStationsMinZoom,
            "maxzoom": PARENT_STOPS_MAX_ZOOM,
            "layout":  {
                "icon-image":            STOP_PARENT_ICON_IMAGE_ID,
                "icon-size":             0.04,
                "icon-anchor":           "bottom",
                "icon-allow-overlap":    true,
                "icon-ignore-placement": true,
                "text-field":            [ "get", "labelShort" ],
                "text-variable-anchor":  [ "top" ],
                "text-radial-offset":    0.3,
                "text-justify":          "auto",
                "text-font":             STOP_LABEL_TEXT_FONT,
                "text-size":             STOP_PARENT_LABEL_TEXT_SIZE,
            },
            "paint": labelPaint,
        },
        {
            "id":      STOPS_LAYER_CHILDREN_ID,
            "type":    "symbol",
            "source":  STOPS_CHILDREN_SOURCE_ID,
            "minzoom": stopPlatformsMinZoom,
            "layout":  {
                "icon-image":            STOP_CHILD_ICON_IMAGE_ID,
                "icon-size":             0.04,
                "icon-anchor":           "bottom",
                "icon-offset":           STOP_CHILD_ICON_OFFSET,
                "icon-allow-overlap":    true,
                "icon-ignore-placement": true,
            },
        },
        {
            "id":      STOPS_LAYER_CHILDREN_NAME_ID,
            "type":    "symbol",
            "source":  STOPS_CHILDREN_SOURCE_ID,
            "minzoom": stopPlatformsMinZoom,
            "layout":  {
                "text-field":         [ "get", "labelShort" ],
                "text-anchor":        "bottom",
                "text-offset":        [ 0, -3 ],
                "text-justify":       "center",
                "text-font":          STOP_LABEL_TEXT_FONT,
                "text-allow-overlap": true,
                "icon-allow-overlap": true,
                "text-size":          STOP_CHILD_LABEL_TEXT_SIZE,
            },
            "paint": labelPaint,
        },
        {
            "id":      STOPS_LAYER_CHILDREN_PLATFORM_ID,
            "type":    "symbol",
            "source":  STOPS_CHILDREN_SOURCE_ID,
            "minzoom": stopPlatformsMinZoom,
            "layout":  {
                "text-field": [
                    "case",
                    [ ">", [ "length", [ "coalesce", [ "get", "platform" ], "" ] ], 0 ],
                    [ "concat", "Quai ", [ "get", "platform" ] ],
                    "",
                ],
                "text-anchor":        "top",
                "text-offset":        [ 0, 0.2 ],
                "text-justify":       "center",
                "text-font":          STOP_LABEL_TEXT_FONT,
                "text-allow-overlap": true,
                "text-size":          STOP_CHILD_LABEL_TEXT_SIZE,
            },
            "paint": labelPaint,
        },
    ];
};

/**
 * Ensures empty GeoJSON sources for parent and platform stops exist on the map.
 *
 * @param map Target MapLibre map instance.
 */
export const ensureStopSources = ( map: maplibregl.Map ): void => {
    if ( map.getSource( STOPS_PARENTS_SOURCE_ID ) === undefined ) {
        map.addSource( STOPS_PARENTS_SOURCE_ID, {
            "type": "geojson",
            "data": EMPTY_STOPS_FEATURE_COLLECTION,
        } );
    }

    if ( map.getSource( STOPS_CHILDREN_SOURCE_ID ) === undefined ) {
        map.addSource( STOPS_CHILDREN_SOURCE_ID, {
            "type": "geojson",
            "data": EMPTY_STOPS_FEATURE_COLLECTION,
        } );
    }
};

/**
 * Adds stop symbol layers when missing, using declarative zoom bounds.
 *
 * @param map Target MapLibre map instance.
 * @param isDarkUi Whether dark UI palette is active.
 */
export const ensureStopLayers = ( map: maplibregl.Map, isDarkUi: boolean ): void => {
    for ( const layerSpec of buildStopLayerSpecs( isDarkUi ) ) {
        if ( map.getLayer( layerSpec.id ) === undefined ) {
            map.addLayer( layerSpec );

            continue;
        }

        const textSize = layerSpec.layout?.["text-size"];
        if ( textSize !== undefined ) {
            map.setLayoutProperty( layerSpec.id, "text-size", textSize );
        }
    }
};

/**
 * Registers stop pole icon sprites on the map.
 *
 * @param map Target MapLibre map instance.
 * @param isDarkUi Whether dark UI palette is active.
 */
export const loadStopMapIcons = async( map: maplibregl.Map, isDarkUi: boolean ): Promise<void> => {
    await loadStopIconSvgAsMapImage( map, STOP_ICON_ASSETS.parent, STOP_PARENT_ICON_IMAGE_ID, isDarkUi );
    await loadStopIconSvgAsMapImage( map, STOP_ICON_ASSETS.child, STOP_CHILD_ICON_IMAGE_ID, isDarkUi );
};

/**
 * Updates stop label paint properties and reloads pole icons for theme changes.
 *
 * @param map Target MapLibre map instance.
 * @param isDarkUi Whether dark UI palette is active.
 */
export const applyStopLayerTheme = async( map: maplibregl.Map, isDarkUi: boolean ): Promise<void> => {
    const stopLabelTextColor = getStopLabelTextColor( isDarkUi );
    const stopLabelTextHaloColor = getStopLabelTextHaloColor( isDarkUi );

    for ( const layerId of STOP_LABEL_TEXT_LAYER_IDS ) {
        if ( map.getLayer( layerId ) !== undefined ) {
            map.setPaintProperty( layerId, "text-color", stopLabelTextColor );
            map.setPaintProperty( layerId, "text-halo-color", stopLabelTextHaloColor );
        }
    }

    await loadStopMapIcons( map, isDarkUi );
};
